import { Fragment } from 'react'
import { Dialog as HeadlessDialog, Transition } from '@headlessui/react'
import { X } from 'lucide-react'
import { cn } from '../../lib/utils'

export function Dialog({ open, onOpenChange, children, className }) {
    return (
        <Transition appear show={open} as={Fragment}>
            <HeadlessDialog as="div" className="relative z-50" onClose={onOpenChange}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/25 dark:bg-black/50" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <HeadlessDialog.Panel className={cn(
                                "w-full max-w-md transform overflow-hidden rounded-2xl text-left align-middle shadow-xl transition-all",
                                className
                            )}>
                                {children}
                            </HeadlessDialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </HeadlessDialog>
        </Transition>
    )
}

export function DialogContent({ className, children, showCloseButton = true, onClose, ...props }) {
    return (
        <div className={cn(
            "relative p-6 bg-white text-gray-900 dark:bg-[#111111] dark:text-white dark:border dark:border-white/5",
            className
        )} {...props}>
            {showCloseButton && (
                <button
                    onClick={onClose}
                    className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
                >
                    <X className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    <span className="sr-only">Close</span>
                </button>
            )}
            {children}
        </div>
    )
}

export function DialogHeader({ className, ...props }) {
    return (
        <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
    )
}

export function DialogFooter({ className, ...props }) {
    return (
        <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-4", className)} {...props} />
    )
}

export function DialogTitle({ className, ...props }) {
    return (
        <HeadlessDialog.Title
            as="h3"
            className={cn("text-lg font-semibold leading-none tracking-tight text-gray-900 dark:text-white", className)}
            {...props}
        />
    )
}

export function DialogDescription({ className, ...props }) {
    return (
        <HeadlessDialog.Description
            as="p"
            className={cn("text-sm text-gray-500 dark:text-gray-400 mt-1", className)}
            {...props}
        />
    )
}
